import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TicketProvider, useTickets } from './context/TicketContext';
import { Toaster } from './components/ui/sonner';
import TicketModal from './components/TicketModal';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalContentProtection from './components/GlobalContentProtection';
import { FocusGuardProvider } from './components/focus-guard/FocusGuardProvider';

// Pages
import Login from './pages/Login';
import TestLogin from './pages/TestLogin';
import Dashboard from './pages/Dashboard';
import Training from './pages/Training';
import Simulation from './pages/Simulation';
import SimulationHistory from './pages/SimulationHistory';
import Topics from './pages/Topics';
import Modules from './pages/Modules';
import Users from './pages/Users';
import Batches from './pages/Batches';
import Leaderboard from './pages/Leaderboard';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Assessments from './pages/Assessments';
import LearningProfile from './pages/LearningProfile';
import VisualAids from './pages/VisualAids';
import ModuleGenerator from './pages/ModuleGenerator';
import Presentations from './pages/Presentations';
import ReferenceDocs from './pages/ReferenceDocs';
import CurriculumBuilder from './pages/CurriculumBuilder';
import InteractiveLearn from './pages/InteractiveLearn';
import JobSimulations from './pages/JobSimulations';
import SkillLearning from './pages/SkillLearning';
import MySimulations from './pages/MySimulations';
import SourcingLabs from './pages/SourcingLabs';
import SourcingLabSession from './pages/SourcingLabSession';
import ATSLogin from './pages/ATSLogin';

// Ticket System Pages
import MyTickets from './pages/MyTickets';
import AdminTickets from './pages/AdminTickets';

// Certificate Verification (Public)
import CertificateVerify from './pages/CertificateVerify';

// Staffing Training Hub
import StaffingTraining from './pages/StaffingTraining';
import EEOCTraining from './pages/EEOCTraining';
import EEOCSession from './pages/EEOCSession';
import QualityTasks from './pages/QualityTasks';
import QualityAdmin from './pages/QualityAdmin';
import CommunicationTraining from './pages/CommunicationTraining';
import LeadershipTraining from './pages/LeadershipTraining';

// EVOLVE Pages
import { 
    EvolveDashboard, 
    EvolveCreateSession, 
    EvolveLiveSession, 
    EvolveFeedback,
    EvolveSessionDetail,
    EvolveHistory,
    EvolveAdminAnalytics
} from './pages/evolve';

// R-PAT Pages
import {
    RPATDashboard,
    RPATLesson,
    RPATAssessment,
    RPATTypingTests,
    RPATPhase,
    RPATCertificate,
    RPATAdminAnalytics,
    RPATPlaybooks,
    RPATPlaybookDetail,
    RPATOverview,
    RPATSimulations,
    RPATGlobalModules,
    RPATGlobalModuleDetail,
    RPATSimulationHistory,
    RPATJobMastery,
    RPATSourcingLabs,
    RPATRolePlayPhase,
    RPATFinalCertification
} from './pages/rpat';

// Inside Sales Training Pages
import {
    InsideSalesDashboard,
    InsideSalesSession,
    InsideSalesAnalytics
} from './pages/inside-sales';

// Role-Play Lab
import { RolePlayLab, RolePlaySession } from './pages/RolePlayLab';
import VoiceRolePlaySession from './pages/VoiceRolePlaySession';

// Gamification
import BadgeCollection from './pages/BadgeCollection';

// Communication Profile
import CommunicationProfile from './pages/CommunicationProfile';

// Speech Profile (Voice Recognition)
import SpeechProfile from './pages/SpeechProfile';

// Maintenance Page
import MaintenancePage from './pages/MaintenancePage';

// Recruiter Simulations Analytics
import RecruiterSimulationsAnalytics from './pages/RecruiterSimulationsAnalytics';

// Job Simulations Analytics
import JobSimulationsAnalytics from './pages/JobSimulationsAnalytics';

// Role Play Lab Analytics
import RolePlayLabAnalytics from './pages/RolePlayLabAnalytics';

// Sourcing Labs Analytics
import SourcingLabsAnalytics from './pages/SourcingLabsAnalytics';

// Evolve/Presentation Analytics
import EvolveAnalytics from './pages/EvolveAnalytics';

// Advanced Recruitment Certification (ARC)
import { ARCDashboard, ARCChapterReader, ARCQuiz, ARCFinalExam, ARCCertificate, ARCPlaybookReader } from './pages/arc';

// Tech Learning LMS
import {
    TechLearningLibrary,
    TechLearningMyCourses,
    TechLearningCourseDetail,
    TechLearningLesson,
    TechLearningCertificate,
    CourseBuilderList,
    CourseEditor,
    CoursePreview,
    LearnerCourseView,
    MyBuilderCourses
} from './pages/tech-learning';

// Finance Training imports
import {
    FinanceLibrary,
    FinanceMyCourses,
    FinanceCourseDetail,
    FinanceLesson,
    FinanceCertificate,
    FinanceCourseBuilderList,
    FinanceCourseEditor,
    FinanceCoursePreview,
    FinanceLearnerCourseView,
    FinanceMyBuilderCourses
} from './pages/finance-training';

// Dynamic (admin-created) course module imports
import {
    ModuleMyCourses,
    ModuleCourseBuilderList,
    ModuleCourseEditor,
    ModuleCoursePreview,
    ModuleLearnerCourseView,
    ModuleScoped
} from './pages/course-modules';

// Maintenance Mode Check Hook
import { useState, useEffect } from 'react';
import axios from 'axios';

const useMaintenanceCheck = () => {
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkMaintenance = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/status`);
                setMaintenanceMode(res.data.enabled);
            } catch (err) {
                console.error('Failed to check maintenance status:', err);
                setMaintenanceMode(false);
            } finally {
                setLoading(false);
            }
        };

        checkMaintenance();
        // Check every 60 seconds
        const interval = setInterval(checkMaintenance, 60000);
        return () => clearInterval(interval);
    }, []);

    return { maintenanceMode, loading };
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const { maintenanceMode, loading: maintenanceLoading } = useMaintenanceCheck();

    if (loading || maintenanceLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check maintenance mode - redirect non-admin users to maintenance page
    if (maintenanceMode && user?.role !== 'admin') {
        return <Navigate to="/maintenance" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } 
            />
            
            {/* Test Login Route - Special route for test user authentication */}
            <Route 
                path="/test-login" 
                element={<TestLogin />} 
            />
            
            {/* ATS Magic Link Login Route */}
            <Route 
                path="/ats-login" 
                element={<ATSLogin />} 
            />
            
            {/* Public Certificate Verification Route */}
            <Route 
                path="/certificate/verify/:certificateNumber" 
                element={<CertificateVerify />} 
            />

            {/* Maintenance Page Route */}
            <Route 
                path="/maintenance" 
                element={<MaintenancePage />} 
            />

            {/* Protected Routes - All Roles */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/leaderboard" 
                element={
                    <ProtectedRoute>
                        <Leaderboard />
                    </ProtectedRoute>
                } 
            />

            {/* Badge Collection Route */}
            <Route 
                path="/badges" 
                element={
                    <ProtectedRoute>
                        <BadgeCollection />
                    </ProtectedRoute>
                } 
            />

            {/* Staffing Training Hub Routes */}
            <Route 
                path="/staffing-training" 
                element={
                    <ProtectedRoute>
                        <StaffingTraining />
                    </ProtectedRoute>
                } 
            />
            
            {/* Communication Training Route */}
            <Route 
                path="/communication-training" 
                element={
                    <ProtectedRoute>
                        <CommunicationTraining />
                    </ProtectedRoute>
                } 
            />
            
            {/* Leadership Training Route */}
            <Route 
                path="/leadership-training" 
                element={
                    <ProtectedRoute>
                        <LeadershipTraining />
                    </ProtectedRoute>
                } 
            />

            {/* User Routes */}
            <Route 
                path="/training" 
                element={
                    <ProtectedRoute allowedRoles={['user', 'trainee']}>
                        <Training />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/simulation" 
                element={
                    <ProtectedRoute allowedRoles={['user', 'trainee', 'admin', 'trainer']}>
                        <Simulation />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assessments" 
                element={
                    <ProtectedRoute>
                        <Assessments />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/learning-profile" 
                element={
                    <ProtectedRoute allowedRoles={['user', 'trainee']}>
                        <LearningProfile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/communication-profile" 
                element={
                    <ProtectedRoute>
                        <CommunicationProfile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/speech-profile" 
                element={
                    <ProtectedRoute>
                        <SpeechProfile />
                    </ProtectedRoute>
                } 
            />

            {/* Admin/Trainer Routes */}
            <Route 
                path="/topics" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <Topics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/modules" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <Modules />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <Reports />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/visual-aids" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <VisualAids />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/module-generator" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <ModuleGenerator />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/presentations" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <Presentations />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reference-docs" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <ReferenceDocs />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/curriculum-builder" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <CurriculumBuilder />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/learn/:topicId" 
                element={
                    <ProtectedRoute>
                        <InteractiveLearn />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/simulation-history" 
                element={
                    <ProtectedRoute>
                        <SimulationHistory />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/job-simulations" 
                element={
                    <ProtectedRoute>
                        <JobSimulations />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/job-simulations-analytics" 
                element={
                    <ProtectedRoute>
                        <JobSimulationsAnalytics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/job-simulations/:skillId" 
                element={
                    <ProtectedRoute>
                        <SkillLearning />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/skill-learning/:skillId" 
                element={
                    <ProtectedRoute>
                        <SkillLearning />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/my-simulations" 
                element={
                    <ProtectedRoute>
                        <MySimulations />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/recruiter-simulations-analytics" 
                element={
                    <ProtectedRoute>
                        <RecruiterSimulationsAnalytics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sourcing-labs" 
                element={
                    <ProtectedRoute>
                        <SourcingLabs />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sourcing-labs-analytics" 
                element={
                    <ProtectedRoute>
                        <SourcingLabsAnalytics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sourcing-labs/session/:sessionId" 
                element={
                    <ProtectedRoute>
                        <SourcingLabSession />
                    </ProtectedRoute>
                } 
            />
            
            {/* EVOLVE Routes */}
            <Route 
                path="/evolve" 
                element={
                    <ProtectedRoute>
                        <EvolveDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/new" 
                element={
                    <ProtectedRoute>
                        <EvolveCreateSession />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/session/:sessionId" 
                element={
                    <ProtectedRoute>
                        <EvolveSessionDetail />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/live/:sessionId" 
                element={
                    <ProtectedRoute>
                        <EvolveLiveSession />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/feedback/:sessionId" 
                element={
                    <ProtectedRoute>
                        <EvolveFeedback />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/history" 
                element={
                    <ProtectedRoute>
                        <EvolveHistory />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve/analytics" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <EvolveAdminAnalytics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/evolve-analytics" 
                element={
                    <ProtectedRoute>
                        <EvolveAnalytics />
                    </ProtectedRoute>
                } 
            />

            {/* Role-Play Lab Routes */}
            <Route 
                path="/roleplay" 
                element={
                    <ProtectedRoute>
                        <RolePlayLab />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/roleplay-analytics" 
                element={
                    <ProtectedRoute>
                        <RolePlayLabAnalytics />
                    </ProtectedRoute>
                } 
            />

            {/* Tech Learning LMS Routes */}
            <Route 
                path="/tech-learning" 
                element={
                    <ProtectedRoute>
                        <TechLearningLibrary />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tech-learning/my-courses" 
                element={
                    <ProtectedRoute>
                        <TechLearningMyCourses />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tech-learning/course/:courseId" 
                element={
                    <ProtectedRoute>
                        <TechLearningCourseDetail />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tech-learning/lesson/:lessonId" 
                element={
                    <ProtectedRoute>
                        <TechLearningLesson />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tech-learning/certificate/:certificateId" 
                element={
                    <ProtectedRoute>
                        <TechLearningCertificate />
                    </ProtectedRoute>
                } 
            />
            
            {/* Course Builder Admin Routes */}
            <Route 
                path="/admin/course-builder" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <CourseBuilderList />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/course-builder/:courseId" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <CourseEditor />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/course-builder/:courseId/preview" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <CoursePreview />
                    </ProtectedRoute>
                } 
            />
            
            {/* Learner Course Builder Routes */}
            <Route 
                path="/tech-learning/builder-courses/my-courses" 
                element={
                    <ProtectedRoute>
                        <MyBuilderCourses />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tech-learning/builder-courses/:courseId" 
                element={
                    <ProtectedRoute>
                        <LearnerCourseView />
                    </ProtectedRoute>
                } 
            />
            
            {/* ========== FINANCE TRAINING ROUTES ========== */}
            <Route 
                path="/finance-training" 
                element={
                    <ProtectedRoute>
                        <FinanceLibrary />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/finance-training/my-courses" 
                element={
                    <ProtectedRoute>
                        <FinanceMyCourses />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/finance-training/course/:courseId" 
                element={
                    <ProtectedRoute>
                        <FinanceCourseDetail />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/finance-training/lesson/:lessonId" 
                element={
                    <ProtectedRoute>
                        <FinanceLesson />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/finance-training/certificate/:certificateId" 
                element={
                    <ProtectedRoute>
                        <FinanceCertificate />
                    </ProtectedRoute>
                } 
            />
            
            {/* Finance Admin Course Builder Routes */}
            <Route 
                path="/admin/finance-course-builder" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <FinanceCourseBuilderList />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/finance-course-builder/:courseId" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <FinanceCourseEditor />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/finance-course-builder/:courseId/preview" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <FinanceCoursePreview />
                    </ProtectedRoute>
                } 
            />
            
            {/* Finance Learner Course Builder Routes */}
            <Route 
                path="/finance-training/builder-courses/my-courses" 
                element={
                    <ProtectedRoute>
                        <FinanceMyBuilderCourses />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/finance-training/builder-courses/:courseId" 
                element={
                    <ProtectedRoute>
                        <FinanceLearnerCourseView />
                    </ProtectedRoute>
                } 
            />
            
            {/* ========== DYNAMIC COURSE MODULE ROUTES ========== */}
            <Route 
                path="/courses/:moduleSlug" 
                element={
                    <ProtectedRoute>
                        <ModuleScoped><ModuleMyCourses /></ModuleScoped>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/courses/:moduleSlug/course/:courseId" 
                element={
                    <ProtectedRoute>
                        <ModuleScoped><ModuleLearnerCourseView /></ModuleScoped>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/module-builder/:moduleSlug" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <ModuleScoped><ModuleCourseBuilderList /></ModuleScoped>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/module-builder/:moduleSlug/:courseId" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <ModuleScoped><ModuleCourseEditor /></ModuleScoped>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/module-builder/:moduleSlug/:courseId/preview" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <ModuleScoped><ModuleCoursePreview /></ModuleScoped>
                    </ProtectedRoute>
                } 
            />
            
            <Route 
                path="/roleplay/:scenarioId" 
                element={
                    <ProtectedRoute>
                        <VoiceRolePlaySession />
                    </ProtectedRoute>
                } 
            />

            {/* R-PAT Routes */}
            <Route 
                path="/rpat" 
                element={
                    <ProtectedRoute>
                        <RPATDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/overview" 
                element={
                    <ProtectedRoute>
                        <RPATOverview />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/lesson/:topicId" 
                element={
                    <ProtectedRoute>
                        <RPATLesson />
                    </ProtectedRoute>
                } 
            />
            {/* Redirect old recruiter-simulation to main simulation page */}
            <Route 
                path="/recruiter-simulation" 
                element={<Navigate to="/simulation" replace />}
            />
            <Route 
                path="/rpat/simulation-history" 
                element={
                    <ProtectedRoute>
                        <RPATSimulationHistory />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/assessment/:assessmentId" 
                element={
                    <ProtectedRoute>
                        <RPATAssessment />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/typing-tests" 
                element={
                    <ProtectedRoute>
                        <RPATTypingTests />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/phase/:phaseId" 
                element={
                    <ProtectedRoute>
                        <RPATPhase />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/certificate" 
                element={
                    <ProtectedRoute>
                        <RPATCertificate />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/playbooks" 
                element={
                    <ProtectedRoute>
                        <RPATPlaybooks />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/playbook/:playbookId" 
                element={
                    <ProtectedRoute>
                        <RPATPlaybookDetail />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/analytics" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <RPATAdminAnalytics />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/simulations" 
                element={
                    <ProtectedRoute>
                        <RPATSimulations />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/eeoc-training" 
                element={
                    <ProtectedRoute>
                        <EEOCTraining />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/eeoc-training/session/:sessionId" 
                element={
                    <ProtectedRoute>
                        <EEOCSession />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/quality-tasks" 
                element={
                    <ProtectedRoute>
                        <QualityTasks />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/quality-admin" 
                element={
                    <ProtectedRoute>
                        <QualityAdmin />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/global" 
                element={
                    <ProtectedRoute>
                        <RPATGlobalModules />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/global/:region" 
                element={
                    <ProtectedRoute>
                        <RPATGlobalModuleDetail />
                    </ProtectedRoute>
                } 
            />

            {/* R-PAT Advanced Certification Path (Phases 5-8) */}
            <Route 
                path="/rpat/certification/job-mastery" 
                element={
                    <ProtectedRoute>
                        <RPATJobMastery />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/certification/sourcing-labs" 
                element={
                    <ProtectedRoute>
                        <RPATSourcingLabs />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/certification/roleplay" 
                element={
                    <ProtectedRoute>
                        <RPATRolePlayPhase />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rpat/certification/final" 
                element={
                    <ProtectedRoute>
                        <RPATFinalCertification />
                    </ProtectedRoute>
                } 
            />

            {/* Advanced Recruitment Certification (ARC) Routes */}
            <Route 
                path="/arc" 
                element={
                    <ProtectedRoute>
                        <ARCDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/arc/chapter/:chapterId" 
                element={
                    <ProtectedRoute>
                        <ARCChapterReader />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/arc/chapter/:chapterId/quiz" 
                element={
                    <ProtectedRoute>
                        <ARCQuiz />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/arc/final-exam" 
                element={
                    <ProtectedRoute>
                        <ARCFinalExam />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/arc/certificate" 
                element={
                    <ProtectedRoute>
                        <ARCCertificate />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/arc/playbook" 
                element={
                    <ProtectedRoute>
                        <ARCPlaybookReader />
                    </ProtectedRoute>
                } 
            />

            {/* Inside Sales Training Routes */}
            <Route 
                path="/inside-sales" 
                element={
                    <ProtectedRoute>
                        <InsideSalesDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/inside-sales/session/:sessionId" 
                element={
                    <ProtectedRoute>
                        <InsideSalesSession />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/inside-sales/analytics" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <InsideSalesAnalytics />
                    </ProtectedRoute>
                } 
            />

            {/* Ticket System Routes */}
            <Route 
                path="/my-tickets" 
                element={
                    <ProtectedRoute>
                        <MyTickets />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/tickets" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminTickets />
                    </ProtectedRoute>
                } 
            />

            {/* Admin Only Routes */}
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <Users />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/batches" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <Batches />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <Settings />
                    </ProtectedRoute>
                } 
            />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

// Error Boundary Wrapper that has access to ticket context
const ErrorBoundaryWrapper = ({ children }) => {
    const { openTicketModal } = useTickets();
    return (
        <ErrorBoundary openTicketModal={openTicketModal}>
            {children}
        </ErrorBoundary>
    );
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <TicketProvider>
                    <BrowserRouter>
                        <ErrorBoundaryWrapper>
                            <GlobalContentProtection />
                            <FocusGuardProvider>
                                <AppRoutes />
                            </FocusGuardProvider>
                        </ErrorBoundaryWrapper>
                        <TicketModal />
                        <Toaster richColors position="top-right" />
                    </BrowserRouter>
                </TicketProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
